import { useMemo, useState } from 'react';
import {
  Modal,
  Select,
  Button,
  Divider,
  Alert,
  Spin,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Flex,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

type ColumnMappingConfirmProps = {
  open: boolean;
  loading: boolean;
  headers: string[];
  suggestedMappings: Record<string, string>;
  availableFields: string[];
  requiredFields: string[];
  onConfirm: (mapping: Record<string, string>) => void;
  onCancel: () => void;
};

const SKIP_VALUE = '__SKIP__';

function buildInitialMapping(params: {
  headers: string[];
  suggestedMappings: Record<string, string>;
  availableFields: string[];
}): Record<string, string> {
  const nextMapping: Record<string, string> = {};

  for (const header of params.headers) {
    const suggestedField = params.suggestedMappings[header];

    if (suggestedField && params.availableFields.includes(suggestedField)) {
      nextMapping[header] = suggestedField;
    }
  }

  return nextMapping;
}

/**
 * Modal component for confirming CSV column to database field mappings
 * Allows users to review Claude suggestions and manually adjust if needed
 */
export function ColumnMappingConfirm({
  open,
  loading,
  headers,
  suggestedMappings,
  availableFields,
  requiredFields,
  onConfirm,
  onCancel,
}: ColumnMappingConfirmProps) {
  const { t } = useTranslation('reports');
  const [mapping, setMapping] = useState<Record<string, string>>(() =>
    buildInitialMapping({
      headers,
      suggestedMappings,
      availableFields,
    }),
  );

  const handleMappingChange = (header: string, field: string) => {
    if (field === SKIP_VALUE) {
      const newMapping = { ...mapping };
      delete newMapping[header];
      setMapping(newMapping);
    } else {
      setMapping({ ...mapping, [header]: field });
    }
  };

  const handleConfirm = () => {
    onConfirm(mapping);
  };

  const mappedFields = new Set(Object.values(mapping).filter(Boolean));
  const missingRequired = requiredFields.filter((f) => !mappedFields.has(f));

  const valueCounts = useMemo(
    () =>
      Object.values(mapping)
        .filter(Boolean)
        .reduce<Record<string, number>>((counts, field) => {
          counts[field] = (counts[field] || 0) + 1;
          return counts;
        }, {}),
    [mapping],
  );

  const hasDuplicateSelections = Object.values(valueCounts).some(
    (count) => count > 1,
  );

  return (
    <Modal
      title={t('assetImport.mappingModal.title')}
      open={open}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleConfirm}
          disabled={missingRequired.length > 0 || hasDuplicateSelections}
        >
          {t('assetImport.mappingModal.confirm')}
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            title={t('assetImport.mappingModal.subtitle')}
            description={t('assetImport.mappingModal.description')}
            type="info"
            showIcon
          />

          {missingRequired.length > 0 && (
            <Alert
              title={t('assetImport.mappingModal.missingRequired')}
              description={`${t('assetImport.mappingModal.missingRequiredDesc')}: ${missingRequired.join(', ')}`}
              type="warning"
              showIcon
            />
          )}

          {hasDuplicateSelections && (
            <Alert
              title={t('assetImport.mappingModal.duplicateFieldMapping')}
              description={t(
                'assetImport.mappingModal.duplicateFieldMappingDesc',
              )}
              type="warning"
              showIcon
            />
          )}

          <Row gutter={16}>
            {headers.map((header) => {
              const selectedField = mapping[header];

              const usedByOtherHeaders = new Set(
                Object.entries(mapping)
                  .filter(
                    ([otherHeader, field]) =>
                      otherHeader !== header && field && field.length > 0,
                  )
                  .map(([, field]) => field),
              );

              const fieldOptions = [
                {
                  label: t('assetImport.mappingModal.skipColumn'),
                  value: SKIP_VALUE,
                },
                ...availableFields
                  .filter(
                    (field) =>
                      !usedByOtherHeaders.has(field) || field === selectedField,
                  )
                  .map((field) => ({
                    label: field,
                    value: field,
                  })),
              ];

              const isDuplicateSelection =
                !!selectedField && (valueCounts[selectedField] || 0) > 1;

              return (
                <Col key={header} xs={24} sm={12} style={{ marginBottom: 16 }}>
                  <Card
                    size="small"
                    style={{
                      borderColor: isDuplicateSelection ? '#faad14' : '#d9d9d9',
                      borderWidth: 1,
                    }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Flex vertical gap={0}>
                        <Text
                          strong
                          style={{
                            fontSize: 12,
                            color: '#666',
                            textTransform: 'uppercase',
                          }}
                        >
                          {t('assetImport.mappingModal.csvColumn')}
                        </Text>
                        <Text style={{ fontSize: 14, fontWeight: 500 }}>
                          {header}
                        </Text>
                      </Flex>

                      <Divider style={{ margin: '0' }} />

                      <Space
                        direction="vertical"
                        style={{ width: '100%' }}
                        size="small"
                      >
                        <Text
                          style={{
                            fontSize: 12,
                            color: '#666',
                          }}
                        >
                          {t('assetImport.mappingModal.mapTo')}
                        </Text>
                        <Select
                          value={selectedField || SKIP_VALUE}
                          onChange={(value) =>
                            handleMappingChange(header, value)
                          }
                          options={fieldOptions}
                          style={{ width: '100%' }}
                        />
                      </Space>

                      {selectedField && !isDuplicateSelection && (
                        <Flex align="center" gap={4}>
                          <CheckOutlined
                            style={{ fontSize: 12, color: '#52c41a' }}
                          />
                          <Text style={{ fontSize: 12, color: '#52c41a' }}>
                            {t('assetImport.mappingModal.mapped')}
                          </Text>
                        </Flex>
                      )}

                      {isDuplicateSelection && (
                        <Text style={{ fontSize: 12, color: '#d48806' }}>
                          {t('assetImport.mappingModal.fieldAlreadyUsed')}
                        </Text>
                      )}
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Space>

        <Divider />

        <Flex>
          <Text strong style={{ fontSize: 12, color: '#999' }}>
            {t('assetImport.mappingModal.requiredFields')}:{' '}
          </Text>
          <Text style={{ fontSize: 12, color: '#999' }}>
            {requiredFields.length > 0 ? requiredFields.join(', ') : '-'}
          </Text>
        </Flex>
      </Spin>
    </Modal>
  );
}
